import { ApiClientsError } from '#pplx/frontend/api-clients/core/errors.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { pricingPlansClient } from '#pplx_generated/typespec/rest/pricing_plans/client.js';
import { useIntl } from 'react-intl';
const PRICING_PLAN_VALUE_PROP_ICONS = {
    'custom-computer': $icon('custom-computer'),
    gift: $icon('gift'),
    refresh: $icon('refresh'),
    check: $icon('check'),
    'carousel-horizontal': $icon('carousel-horizontal'),
    browser: $icon('browser'),
    'chart-bar': $icon('chart-bar'),
    apps: $icon('apps'),
    'trending-up': $icon('trending-up'),
};
export function getPricingPlanValuePropIcon(icon) {
    if (!Object.hasOwn(PRICING_PLAN_VALUE_PROP_ICONS, icon))
        return $icon('check');
    return PRICING_PLAN_VALUE_PROP_ICONS[icon] ?? $icon('check');
}
const PRICING_PLANS_QUERY_KEY = ['/rest/pricing-plans'];
const PRICING_PLANS_STALE_TIME_MS = 60 * 1000;
export function getPricingPlansQueryKey(locale) {
    return locale ? [...PRICING_PLANS_QUERY_KEY, locale] : PRICING_PLANS_QUERY_KEY;
}
export function pricingPlansQueryOptions({ displayLocale, headers, reason = 'pricing-plans', } = {}) {
    const requestHeaders = Object.fromEntries(new Headers(headers));
    if (displayLocale)
        requestHeaders['accept-language'] = displayLocale;
    const requestOptions = {
        headers: requestHeaders,
        ...(displayLocale && { params: { query: { locale: displayLocale } } }),
    };
    return {
        queryKey: getPricingPlansQueryKey(displayLocale),
        queryFn: async () => {
            const { data, error, response } = await pricingPlansClient.GET('/rest/pricing-plans', reason, requestOptions);
            if (error || !data) {
                throw new ApiClientsError('API_CLIENTS_ERROR', {
                    message: 'Failed to get pricing plans',
                    cause: error,
                    status: response.status ?? 0,
                });
            }
            return data;
        },
        retry: false,
        staleTime: PRICING_PLANS_STALE_TIME_MS,
    };
}
export function usePricingPlans({ displayLocale } = {}) {
    const { locale } = useIntl();
    const { session, status } = useSession();
    // Ask persists the active locale in `pplx.chosen-locale`, which the backend prefers over
    // Accept-Language. Marketing passes its route locale explicitly because it can differ from
    // the browser locale. In both cases, the locale key ensures language changes trigger a refetch.
    const query = useQuery({
        ...pricingPlansQueryOptions({ displayLocale }),
        enabled: status !== 'loading',
        queryKey: [
            ...getPricingPlansQueryKey(displayLocale ?? locale),
            session?.user.id ?? 'anonymous',
            session?.user.org_uuid ?? 'none',
        ],
    });
    return query;
}
export function getPricingPlan(response, tier) {
    return response?.plans.find((plan) => plan.tier === tier);
}
//# sourceMappingURL=pricingPlans.js.map